import { useEffect, useMemo, useState } from 'react'
import { Card, Badge } from '@/components/ui'
import { listDocumentsRegistry } from '@/services/documentsRegistryService'
import type { StudioDocumentsRegistryRow } from '@/types'
import {
  CONTRACT_STATUS_LABELS,
  EXPIRY_BADGE,
  contractStatusTone,
  deriveContractStatus,
  expirySortKey,
  fmtDate,
  formatFranchiseeLabel,
  getContractDates,
  getExpiryInfo,
  isActiveOrRecognized,
  matchesExpiryFilter,
  type ExpiryFilter,
} from '@/lib/documentsRegistry'
import { FileText, Search, AlertTriangle } from 'lucide-react'

const GRID_COLS =
  'grid-cols-[64px_minmax(210px,1.7fr)_minmax(100px,0.85fr)_minmax(130px,1fr)_minmax(160px,1fr)_minmax(180px,1.15fr)]'

/**
 * Витрина реестра документов — то, что видят все, у кого нет расширенного
 * доступа УК (развилка — в `DocumentsRegistryPage`).
 *
 * Отличия от расширенного экрана:
 * - популяция жёстко сужена до действующих и признаваемых действующими ДКК
 *   (`isActiveOrRecognized`), переключателя «показать все» нет;
 * - строка не кликабельна — провала в карточку договора нет;
 * - нет ни одного действия, меняющего данные (в т.ч. «Новый ДКК»).
 *
 * Это витрина, а не гейт: `list_studio_documents_registry` — SECURITY DEFINER
 * без собственной проверки прав, поэтому сужение здесь чисто презентационное.
 * Карточку договора закрывает маршрутный `AdvancedManagementCompanyRoute`.
 */
export function DocumentsRegistryShowcase() {
  const [rows, setRows] = useState<StudioDocumentsRegistryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [expiryFilter, setExpiryFilter] = useState<ExpiryFilter>('ALL')
  const [sortByExpiry, setSortByExpiry] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const handle = setTimeout(() => {
      // Без limit сервис постранично тянет весь реестр: PostgREST max-rows = 1000
      // на PROD режет любой одиночный ответ, а ДКК уже больше 1000. Отбор
      // «действующие» — клиентский, поэтому обрезка ответа съела бы часть витрины.
      listDocumentsRegistry({ search })
        .then(data => {
          if (!cancelled) {
            setRows(data.filter(isActiveOrRecognized))
            setError(null)
          }
        })
        .catch(e => {
          if (!cancelled) setError(e instanceof Error ? e.message : String(e))
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [search])

  const summary = useMemo(() => {
    const active = rows.filter(r => deriveContractStatus(r) === 'ACTIVE_SIGNED').length
    return { total: rows.length, active, recognized: rows.length - active }
  }, [rows])

  const visibleRows = useMemo(() => {
    let out = expiryFilter === 'ALL' ? rows : rows.filter(r => matchesExpiryFilter(r, expiryFilter))
    if (sortByExpiry) out = [...out].sort((a, b) => expirySortKey(a) - expirySortKey(b))
    return out
  }, [rows, expiryFilter, sortByExpiry])

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText size={20} className="text-primary" />
          <h1 className="text-lg font-semibold">Реестр документов</h1>
        </div>
        <div className="text-xs text-muted-foreground text-right">
          <div>{summary.total} ДКК · действует {summary.active} · признаётся {summary.recognized}</div>
          <div className="mt-0.5">Показаны только действующие договоры</div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Франчайзи, студия, город, № ДКК, ИНН…"
            className="w-full pl-9 pr-3 py-2 border border-border rounded-md bg-background text-sm"
          />
        </div>
        <select
          value={expiryFilter}
          onChange={e => setExpiryFilter(e.target.value as ExpiryFilter)}
          className="rounded-md border border-border px-3 py-2 text-sm bg-background"
          title="Фильтр по сроку окончания"
        >
          <option value="ALL">Все сроки</option>
          <option value="EXPIRING">Истекающие (≤6 мес)</option>
          <option value="OVERDUE">Просрочен</option>
          <option value="LT_1M">До 1 мес</option>
          <option value="LT_3M">До 3 мес</option>
          <option value="LT_6M">До 6 мес</option>
          <option value="NO_DATE">Без даты окончания</option>
        </select>
        <label className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer select-none bg-background">
          <input
            type="checkbox"
            checked={sortByExpiry}
            onChange={e => setSortByExpiry(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
          <span>По сроку окончания</span>
        </label>
      </div>

      {error && (
        <div className="text-sm text-destructive flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Загрузка…</div>
      ) : visibleRows.length === 0 ? (
        <div className="text-sm text-muted-foreground">Ничего не найдено.</div>
      ) : (
        <Card className="py-2 overflow-x-auto">
          <div className="min-w-[900px]">
            <div className={`grid ${GRID_COLS} gap-2 px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border`}>
              <div>Студия</div>
              <div>Франчайзи</div>
              <div>Город</div>
              <div>№ ДКК</div>
              <div>Статус договора</div>
              <div>Срок ДКК</div>
            </div>
            {visibleRows.map(row => {
              const status = deriveContractStatus(row)
              const dates = getContractDates(row)
              const expiry = getExpiryInfo(row)
              return (
                <div
                  key={row.contract_id}
                  className={`grid ${GRID_COLS} gap-2 px-3 py-2 border-b border-border last:border-0 items-center`}
                >
                  <div className="text-sm font-semibold">{row.studio_number ?? '—'}</div>
                  <div className="min-w-0">
                    <div className="text-sm truncate">{formatFranchiseeLabel(row)}</div>
                    <div className="text-xs text-muted-foreground truncate">{row.partner_legal_name ?? ''}</div>
                  </div>
                  <div className="text-sm truncate">{row.city_name ?? '—'}</div>
                  <div className="text-sm truncate">{row.contract_number ?? '—'}</div>
                  <div>
                    <Badge variant={contractStatusTone(status)}>
                      {row.legal_status_label ?? CONTRACT_STATUS_LABELS[status]}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <div>{fmtDate(dates.effectiveFrom)} — {fmtDate(dates.actualEnd ?? dates.expectedEnd)}</div>
                    {expiry.category !== 'NONE' && expiry.category !== 'OK' && (
                      <Badge variant={EXPIRY_BADGE[expiry.category].tone} className="mt-1 px-2 py-0.5 text-xs">
                        {EXPIRY_BADGE[expiry.category].label}
                        {expiry.daysLeft != null && expiry.category !== 'NO_DATE'
                          ? ` · ${expiry.category === 'OVERDUE' ? Math.abs(expiry.daysLeft) : expiry.daysLeft} дн.`
                          : ''}
                      </Badge>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      )}
    </div>
  )
}
